import { useState, useEffect } from 'react';
import { pcfAPI, approvalsAPI } from '@/lib/api';
import { track, EVENTS } from '@/lib/telemetry';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Calculator, TrendingDown, TrendingUp, Download, RefreshCw, Loader2, History, CheckCircle2, XCircle, Clock, ShieldCheck } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import ApprovalHistoryDialog from '@/components/ApprovalHistoryDialog';
import VerificationLinkDialog from '@/components/VerificationLinkDialog';
import WhyThisNumberModal from '@/components/WhyThisNumberModal';
import PassportVersionHistory from '@/components/PassportVersionHistory';

const STATUS_STYLES = {
  approved: { cls: 'bg-green-100 text-green-800 border border-green-300', icon: CheckCircle2, label: 'Approved' },
  rejected: { cls: 'bg-red-100 text-red-800 border border-red-300', icon: XCircle, label: 'Rejected' },
  pending:  { cls: 'bg-amber-100 text-amber-800 border border-amber-300', icon: Clock, label: 'Pending review' },
};

function ReviewBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <span
      data-testid={`review-status-badge-${status || 'pending'}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {s.label}
    </span>
  );
}

export default function PCF() {
  const { user } = useAuth();
  const canApprove = ['admin', 'editor'].includes(user?.role);
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [whyMaterial, setWhyMaterial] = useState(null);

  const fetchProducts = () => {
    pcfAPI.getProducts().then(r => {
      setProducts(r.data);
      if (!selectedId && r.data.length > 0) setSelectedId(r.data[0].id);
    });
  };
  useEffect(() => { fetchProducts(); }, []);

  const fetchDetail = () => {
    if (!selectedId) return;
    setLoading(true);
    pcfAPI.getDetail(selectedId).then(r => { setDetail(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchDetail(); }, [selectedId]);

  const handleCalculate = async () => {
    if (!selectedId) return;
    setLoading(true);
    await pcfAPI.calculate(selectedId);
    track(EVENTS.PCF_CALCULATED, { product_id: selectedId });
    pcfAPI.getDetail(selectedId).then(r => { setDetail(r.data); setLoading(false); });
  };

  const handleApprove = async (action) => {
    if (!selectedId) return;
    setApproving(true);
    try {
      await approvalsAPI.create(selectedId, action, '');
      toast.success(action === 'approve' ? 'PCF approved — export now unlocked' : 'PCF rejected');
      fetchDetail();
      fetchProducts();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to record approval.';
      toast.error(msg);
    } finally {
      setApproving(false);
    }
  };

  const isApproved = detail?.review_status === 'approved';
  const isAdmin = user?.role === 'admin';
  const exportAllowed = isApproved || isAdmin;
  const tier = (user?.subscription_tier || 'basic').toLowerCase();
  const complianceAllowed = isAdmin || ['pro', 'advanced', 'enterprise'].includes(tier);
  const [auditExporting, setAuditExporting] = useState(false);
  const [euExporting, setEuExporting] = useState(false);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    if (!selectedId) { toast.error('Please select a product first'); return; }
    if (!exportAllowed) { toast.error('This PCF record must be approved by a human reviewer before it can be exported.'); return; }
    setExporting(true);
    try {
      const res = await pcfAPI.exportPdf(selectedId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const safeName = (detail?.product_name || 'product').replace(/\s+/g, '_').slice(0, 40);
      downloadBlob(blob, `Veridex_PCF_${safeName}.pdf`);
      toast.success('Simple PDF exported');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to export PDF. Please try again.';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const handleExportAuditPdf = async () => {
    if (!selectedId) { toast.error('Please select a product first'); return; }
    if (!exportAllowed) { toast.error('Human approval required before generating the Compliance Report.'); return; }
    if (!complianceAllowed) {
      toast.error('The Compliance Report is available on the Pro, Advanced and Enterprise plans.');
      return;
    }
    setAuditExporting(true);
    try {
      const res = await pcfAPI.exportAuditPdf(selectedId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const safeName = (detail?.product_name || 'product').replace(/\s+/g, '_').slice(0, 40);
      downloadBlob(blob, `Veridex_Compliance_PCF_${safeName}.pdf`);
      toast.success('Compliance report exported');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to export compliance report.';
      toast.error(msg);
    } finally {
      setAuditExporting(false);
    }
  };

  const handleExportEuReport = async () => {
    if (!selectedId) { toast.error('Please select a product first'); return; }
    if (!exportAllowed) { toast.error('Human approval required before generating the EU Export Report.'); return; }
    if (!complianceAllowed) {
      toast.error('The EU Export Report is available on the Pro, Advanced and Enterprise plans.');
      return;
    }
    setEuExporting(true);
    try {
      const res = await pcfAPI.exportEuReport(selectedId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const safeName = (detail?.product_name || 'product').replace(/\s+/g, '_').slice(0, 40);
      downloadBlob(blob, `Veridex_EU_Export_Report_${safeName}.pdf`);
      toast.success('EU Export report downloaded');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to export EU report.';
      toast.error(msg);
    } finally {
      setEuExporting(false);
    }
  };

  const chartData = detail ? [
    { name: 'Energy', value: detail.contributions.energy },
    { name: 'Process', value: detail.contributions.process },
    { name: 'Materials', value: detail.contributions.materials },
  ] : [];

  return (
    <div data-testid="pcf-page" className="space-y-6">
      <div className="cp-page-header">
        <h1 className="cp-page-title">Product Carbon Footprint</h1>
        <div className="flex gap-2 flex-wrap items-center">
          {detail && <ReviewBadge status={detail.review_status} />}
          {canApprove && detail && detail.review_status !== 'approved' && (
            <Button
              variant="outline"
              onClick={() => handleApprove('approve')}
              disabled={approving || !selectedId}
              className="border-green-500/50 text-green-700 hover:bg-green-50 dark:hover:bg-green-950/40"
              data-testid="approve-pcf-btn"
            >
              {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Approve PCF
            </Button>
          )}
          {canApprove && detail && detail.review_status === 'approved' && (
            <Button
              variant="outline"
              onClick={() => handleApprove('reject')}
              disabled={approving || !selectedId}
              className="border-red-500/50 text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
              data-testid="reject-pcf-btn"
            >
              {approving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Revoke Approval
            </Button>
          )}
          <Button variant="outline" onClick={() => setHistoryOpen(true)} disabled={!selectedId} data-testid="view-approval-history-btn">
            <History className="w-4 h-4 mr-2" /> Approval History
          </Button>
          <Button
            variant="outline"
            onClick={() => setVerifyOpen(true)}
            disabled={!selectedId || !exportAllowed}
            title={!exportAllowed ? 'Approve the PCF first to issue a public verification link' : 'Issue a state-bound, cryptographically signed public verification link'}
            className={exportAllowed ? 'border-green-500/50 text-green-700 hover:bg-green-50 dark:hover:bg-green-950/40' : ''}
            data-testid="issue-verification-link-btn"
          >
            <ShieldCheck className="w-4 h-4 mr-2" /> Verification Link
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPdf}
            disabled={!selectedId || exporting || loading || !exportAllowed}
            title={!exportAllowed ? 'Human approval required to export' : 'Simple 1-page PDF'}
            data-testid="export-pdf-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {exporting ? 'Exporting…' : 'Export Simple PDF'}
          </Button>
          <Button
            variant="outline"
            onClick={handleExportAuditPdf}
            disabled={!selectedId || auditExporting || loading || !exportAllowed || !complianceAllowed}
            title={
              !complianceAllowed ? 'Upgrade to Pro / Advanced / Enterprise to unlock the Compliance Report'
              : !exportAllowed ? 'Human approval required to export'
              : 'Audit-grade report with Executive Summary, dynamic Data Quality and EF freshness checks'
            }
            className={complianceAllowed ? 'border-primary/50 text-primary hover:bg-primary/5' : ''}
            data-testid="export-compliance-btn"
          >
            {auditExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {auditExporting ? 'Exporting…' : 'Export Compliance Report'}
          </Button>
          <Button
            variant="outline"
            onClick={handleExportEuReport}
            disabled={!selectedId || euExporting || loading || !exportAllowed || !complianceAllowed}
            title={
              !complianceAllowed ? 'Upgrade to Pro / Advanced / Enterprise to unlock the EU Export Report'
              : !exportAllowed ? 'Human approval required to export'
              : '3-page EU export-ready report (ISO 14067 + CBAM input compatible)'
            }
            className={complianceAllowed ? 'border-primary/50 text-primary hover:bg-primary/5' : ''}
            data-testid="export-eu-report-btn"
          >
            {euExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {euExporting ? 'Exporting…' : 'Export EU Ready Report'}
          </Button>
          <Button onClick={handleCalculate} className="bg-primary hover:bg-primary/90" data-testid="calculate-pcf-btn" disabled={!selectedId || loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Calculate PCF
          </Button>
        </div>
      </div>

      {detail && !exportAllowed && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-200"
          data-testid="pcf-review-notice"
        >
          <Clock className="h-4 w-4 mt-0.5 shrink-0" />
          <p>
            <span className="font-semibold">Human review required.</span>{' '}
            PDF exports and CBAM filings for this product are locked until an editor or admin approves the current PCF value.
          </p>
        </div>
      )}

      <ApprovalHistoryDialog
        productId={selectedId}
        productName={detail?.product_name}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />

      <VerificationLinkDialog
        productId={selectedId}
        productName={detail?.product_name}
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
      />

      <WhyThisNumberModal
        open={!!whyMaterial}
        onOpenChange={(v) => !v && setWhyMaterial(null)}
        material={whyMaterial}
        product={detail}
      />

      {/* Product Selector */}
      <div className="max-w-xs">
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger data-testid="pcf-product-select"><SelectValue placeholder="Select Product" /></SelectTrigger>
          <SelectContent>
            {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {detail && (
        <div className="space-y-6 animate-fade-in">
          {/* PCF Value Card */}
          <Card className="p-6" data-testid="pcf-value-card">
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Calculator className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{detail.product_name} - Current PCF</p>
                  <p className="text-3xl font-heading font-bold">{detail.pcf_value} <span className="text-base font-normal text-muted-foreground">tCO2/ton</span></p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted">
                <span className="text-xs text-muted-foreground">vs last month</span>
                <span className={`flex items-center gap-1 text-sm font-semibold ${detail.change_percent <= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {detail.change_percent <= 0 ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                  {Math.abs(detail.change_percent)}%
                </span>
              </div>
              <div className="text-sm text-muted-foreground">
                Previous: <span className="font-mono font-semibold text-foreground">{detail.previous_pcf}</span> tCO2/ton
              </div>
            </div>
          </Card>

          {/* Contributions Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="cp-chart-card" data-testid="pcf-contributions-chart">
              <h3 className="text-sm font-heading font-semibold mb-4">Emission Contributions</h3>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} width={70} />
                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="value" fill="#166534" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Raw Materials Table */}
            <div className="cp-chart-card" data-testid="pcf-materials-table">
              <h3 className="text-sm font-heading font-semibold mb-4">Raw Materials</h3>
              <div className="cp-table-wrapper">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Material</TableHead>
                      <TableHead className="text-right">Qty/Unit</TableHead>
                      <TableHead className="text-right">EF</TableHead>
                      <TableHead className="text-right">Emissions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.raw_materials.map((m, i) => (
                      <TableRow key={m.name ? `${m.name}-${i}` : i}>
                        <TableCell className="font-medium text-sm">
                          <button
                            type="button"
                            onClick={() => setWhyMaterial(m)}
                            className="underline decoration-dotted underline-offset-2 hover:text-primary text-left"
                            data-testid={`why-material-btn-${i}`}
                            title="Why this number?"
                          >
                            {m.name}
                          </button>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{m.quantity_per_unit}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{m.emission_factor}</TableCell>
                        <TableCell className="text-right font-mono font-semibold text-sm">{m.total_emissions}</TableCell>
                      </TableRow>
                    ))}
                    {detail.raw_materials.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground text-sm">No raw materials data</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedId && (
        <div data-testid="pcf-passport-version-history">
          <PassportVersionHistory productId={selectedId} />
        </div>
      )}
    </div>
  );
}
